import { EventEmitter } from 'events'
import {
  EngineUndoRedo,
  GroupExpanded,
  MaterialParam,
  EngineMode,
  MaterialColorParam,
  RemoteUserLocation,
  RemoteUserStatus,
  LocalUserLocation,
  SceneNavigatorEventPayload,
  EngineStackReorderingOpts,
  EngineExportCapturePayload,
  EngineCapturePayload,
  DeviceStatusEventPayload,
  ModelLoadedStatusEventPayload,
  SelectedObjectUI,
  PropertyPanelId,
  PropertyPanelSetter,
  Vec3d,
  PropertyPathVariant,
  PropertyPanelPath,
  IntGuard,
  BoolGuard,
  EnumBaseGuard,
  FloatGuard,
  StringGuard,
  Vec3dGuard,
  Vec2d,
  Vec2dGuard,
  PropertyHasChangedData,
  IVec2Guard,
  IVec2,
  PropertyListHasChangedData,
  SceneContentData,
  PropertyPanelSetStatus,
  IVec3,
  IVec3Guard,
  PropertySoftBoundsHaveChangedData,
  EngineCommitChange,
  RequestFontPayload,
  SceneLayer,
  Engine2DPrimitive
} from './types'
import { CursorRegistry, Cursor } from '@services/engine/cursorRegistry'

import {
  DocumentEventObserver,
  KeyboardEventListener,
  MouseEventListener
} from './documentEventObserver'

import {
  isTextFieldActive,
  parseValidEngineJson,
  traceInvalidJsonError
} from './utils'
import AdobeIMS from '@services/auth/IMS'

import { ITokenInformation } from '@identity/imslib/adobe-id/custom-types/CustomTypes'
import { captureException } from '@sentry/nextjs'
import { SnapshotListHasChangedData } from './types'
import { RootStore } from '@store/store'

export const CANVAS_ID = 'canvas'

export let cursorRegistry = new CursorRegistry()

export const CANVAS_CONTAINER_ID = 'canvas-container'

const ACTIVE_INPUT_PASSTHROUGH_SHORTCUTS = [
  ['control', 'd'],
  ['control', 'f'],
  ['control', 'shift', 'h'],
  ['control', '-'],
  ['control', '+'],
  ['control', '='],
  ['control', '=', '-']
]

const isShortcutMatch = (shortcut: string[], keys: string[]): boolean => {
  return (
    shortcut.length === keys.length && shortcut.every(key => keys.includes(key))
  )
}

export const defaultPropertyPanelId: PropertyPanelId = ''

export class Engine {
  public EngineStatusChannel: EventEmitter = new EventEmitter()
  public EngineDataChannel: EventEmitter = new EventEmitter()
  public EnginePropertyHasChangedChannel: EventEmitter = new EventEmitter()
  public EnginePropertyListHasChangedChannel: EventEmitter = new EventEmitter()
  public EnginePropertySoftBoundsHaveChangedChannel: EventEmitter =
    new EventEmitter()
  public EngineSceneContentChannel: EventEmitter = new EventEmitter()
  public EngineDebugChannel: EventEmitter = new EventEmitter()
  public EngineFontChannel: EventEmitter = new EventEmitter()
  public EngineSnapshotListHasChangedChannel = new EventEmitter()
  public EngineSnapshotRenamedChannel = new EventEmitter()
  private EngineInstance: any
  private EngineFeatures: string = ''
  public initialized: boolean = false
  public targetCanvas?: HTMLCanvasElement
  private animationFrameRef?: number
  private globalKeyboardListener: KeyboardEventListener
  private globalMouseListener: MouseEventListener
  private allowedKeyboardKeys: string[] = ['/', 'Escape']
  private currentPressedKeys: string[] = []
  public enginePropertyHasChangedChannelKey: string = 'propertyHasChangedData'
  public enginePropertyListHasChangedChannelKey: string =
    'propertyListHasChangedData'
  public enginePropertySoftBoundsHaveChangedChannelKey: string =
    'propertySoftBoundsHaveChangedData'
  public engineSceneContentChannelKey: string = 'nodeData'
  public engineSnapshotListHasChangedChannelKey: string =
    'snapshotListHasChangedData'
  public engineSnapshotRenamedChannelKey: string = 'snapshotRenamedData'
  public engineFrameDataChannelKey: string = 'frameData'
  private renderOnlyFrameContent: boolean = false

  constructor() {
    this.initialize = this.initialize.bind(this)
    this.initializeCanvas = this.initializeCanvas.bind(this)
    this.initializeEngine = this.initializeEngine.bind(this)
    this.isAllowedKeyboardKey = this.isAllowedKeyboardKey.bind(this)
    this.animate = this.animate.bind(this)
    this.appendCanvas = this.appendCanvas.bind(this)
    this.unmount = this.unmount.bind(this)
    this.mount = this.mount.bind(this)
    this.updateFeatures = this.updateFeatures.bind(this)
    this.loadDocument = this.loadDocument.bind(this)
    this.getDocument = this.getDocument.bind(this)
    this.setLocalUserLocation = this.setLocalUserLocation.bind(this)
    this.getRemoteUserCursorPosition =
      this.getRemoteUserCursorPosition.bind(this)
    this.setRemoteUserStatus = this.setRemoteUserStatus.bind(this)
    this.setRemoteUserLocation = this.setRemoteUserLocation.bind(this)
    this.toggleUsersAvatarVisibility =
      this.toggleUsersAvatarVisibility.bind(this)
    this.duplicatePrimitive = this.duplicatePrimitive.bind(this)
    this.deletePrimitive = this.deletePrimitive.bind(this)
    this.setPrimitiveScaleCorners = this.setPrimitiveScaleCorners.bind(this)
    this.recenterCamera = this.recenterCamera.bind(this)
    this.handleUndoRedo = this.handleUndoRedo.bind(this)
    this.toggleShowObjectsOutline = this.toggleShowObjectsOutline.bind(this)
    this.setModelPose = this.setModelPose.bind(this)
    this.setAutoFocusEnabled = this.setAutoFocusEnabled.bind(this)
    this.setSnapping = this.setSnapping.bind(this)
    this.isSnappingEnable = this.isSnappingEnable.bind(this)
    this.hexColorToEngineColor = this.hexColorToEngineColor.bind(this)
    this.rGBToHex = this.rGBToHex.bind(this)
    this.exportCapture = this.exportCapture.bind(this)
    this.reorderStackElements = this.reorderStackElements.bind(this)
    this.setHistoryMode = this.setHistoryMode.bind(this)
    this.setHistorySliderPos = this.setHistorySliderPos.bind(this)
    this.importSVG = this.importSVG.bind(this)
    this.goDownGroupHierarchy = this.goDownGroupHierarchy.bind(this)
    this.goUpGroupHierarchy = this.goUpGroupHierarchy.bind(this)
    this.mouseEnterObject = this.mouseEnterObject.bind(this)
    this.mouseLeaveObject = this.mouseLeaveObject.bind(this)

    // Event listeners
    const self = this

    this.globalKeyboardListener = {
      onKeyDown: (e: KeyboardEvent): boolean => {
        // add the key to the current pressed shortcut
        this.addToPressedKeys(e.key.toLowerCase())

        // some shortcuts are passed through to the engine even if a text field is active
        if (
          isTextFieldActive() &&
          !ACTIVE_INPUT_PASSTHROUGH_SHORTCUTS.some(shortcut =>
            isShortcutMatch(shortcut, this.currentPressedKeys)
          )
        ) {
          return false
        }

        // dispatch first on engine, then if not handled let it go
        if (self.EngineInstance?.Command_HandleKeyDown(e)) {
          if (this.isAllowedKeyboardKey(e.key)) {
            return true
          }

          e.stopImmediatePropagation()
          e.preventDefault()
          return true
        }

        return false
      },

      onKeyUp: (e: KeyboardEvent): boolean => {
        // remove the key from the current pressed shortcut
        this.removeFromPressedKeys(e.key.toLowerCase())

        // some shortcuts are passed through to the engine even if a text field is active
        if (
          isTextFieldActive() &&
          !ACTIVE_INPUT_PASSTHROUGH_SHORTCUTS.some(shortcut =>
            isShortcutMatch(shortcut, this.currentPressedKeys)
          )
        ) {
          return false
        }

        // dispatch first on engine, then if not handled let it go
        if (self.EngineInstance?.Command_HandleKeyUp(e)) {
          if (this.isAllowedKeyboardKey(e.key)) {
            return true
          }

          e.stopImmediatePropagation()
          e.preventDefault()
          return true
        }

        return false
      }
    }

    this.globalMouseListener = {
      onMouseDown: (e: MouseEvent): boolean => {
        if (e.target === this.targetCanvas) {
          if (
            self.EngineInstance?.Command_HandleMouseDown(
              e.offsetX,
              e.offsetY,
              e
            )
          ) {
            DocumentEventObserver.instance()?.startCapture(e)

            e.stopImmediatePropagation()
            return true
          }
        }

        return false
      },

      onMouseMoved: (e: MouseEvent): boolean => {
        const info = DocumentEventObserver.instance()?.mouseCaptureInfo()

        if (info && this.targetCanvas) {
          const posX = info.localX(this.targetCanvas)
          const posY = info.localY(this.targetCanvas)
          self.EngineInstance?.Command_HandleMouseMoved(posX, posY, e)

          e.preventDefault()
          return true
        } else {
        }

        return false
      },

      onMouseUp: (e: MouseEvent): boolean => {
        const info = DocumentEventObserver.instance()?.mouseCaptureInfo()

        if (info && this.targetCanvas) {
          const posX = info.localX(this.targetCanvas)
          const posY = info.localY(this.targetCanvas)
          self.EngineInstance?.Command_HandleMouseUp(posX, posY, e)

          if (info.source == this.targetCanvas) {
            DocumentEventObserver.instance()?.stopCapture()
          }

          e.stopImmediatePropagation()
          return true
        }

        return false
      }
    }

    window.addEventListener('blur', this.clearPressedKeys)
  }

  public async initialize(features: string): Promise<void> {
    if (this.initialized) return
    this.targetCanvas = this.initializeCanvas()
    const { default: EngineModule } = await import('./engineModule')
    const { default: EngineModuleInstance } = await EngineModule(
      this.targetCanvas
    )
    this.EngineFeatures = features
    await this.initializeEngine(EngineModuleInstance)

    this.observeDocumentEvents()

    this.initialized = true
  }

  private static keyboardEventListenerID = 'EngineKeyboardEventListener'
  private static mouseEventListenerID = 'EngineMouseEventListener'

  private isAllowedKeyboardKey(key: string): boolean {
    return this.allowedKeyboardKeys.includes(key)
  }

  private addToPressedKeys(key: string): void {
    if (key === 'meta') {
      // Treat 'meta' as 'control' for MacOS
      key = 'control'
    }
    if (!this.currentPressedKeys.includes(key)) {
      this.currentPressedKeys.push(key)
    }
  }

  private removeFromPressedKeys(key: string): void {
    if (key === 'meta') {
      // See https://issues.chromium.org/issues/40880986 (Missing keyup event with Command key on macOS )
      this.clearPressedKeys()
      return
    }
    const index = this.currentPressedKeys.indexOf(key)
    if (index > -1) {
      this.currentPressedKeys.splice(index, 1)
    }
  }

  private clearPressedKeys = (): void => {
    this.currentPressedKeys = []
  }

  private observeDocumentEvents(): void {
    DocumentEventObserver.instance()?.addKeyboardEventListener(
      Engine.keyboardEventListenerID,
      this.globalKeyboardListener
    )

    DocumentEventObserver.instance()?.addMouseEventListener(
      Engine.mouseEventListenerID,
      this.globalMouseListener
    )
  }

  private unobserveDocumentEvents() {
    DocumentEventObserver.instance()?.removeKeyboardEventListener(
      Engine.keyboardEventListenerID
    )

    DocumentEventObserver.instance()?.removeMouseEventListener(
      Engine.mouseEventListenerID
    )
  }

  private initializeCanvas(): HTMLCanvasElement {
    const devicePixelRatio = window.devicePixelRatio || 1
    const targetCanvas = document.createElement('canvas')
    targetCanvas.id = CANVAS_ID
    targetCanvas.addEventListener('contextmenu', ev => ev.preventDefault())

    this.appendCanvas(targetCanvas)

    const xres = Math.round(targetCanvas.offsetWidth * devicePixelRatio) | 0
    const yres = Math.round(targetCanvas.offsetHeight * devicePixelRatio) | 0
    console.log(
      `React  : Setting canvas to ${xres} x ${yres} for CSS  ${targetCanvas.clientWidth} x ${targetCanvas.offsetHeight} (dpi = ${devicePixelRatio})`
    )
    targetCanvas.width = xres
    targetCanvas.height = yres

    // needed for focus() bellow to work
    targetCanvas.setAttribute('tabindex', '0')
    targetCanvas.focus()

    targetCanvas.addEventListener('click', () => {
      targetCanvas.focus()
    })

    return targetCanvas
  }

  private appendCanvas(canvas: HTMLCanvasElement) {
    const canvasContainer = document.getElementById(CANVAS_CONTAINER_ID)
    if (!canvasContainer) throw new Error('missing canvas container')
    canvasContainer.appendChild(canvas)
  }

  public unmount() {
    this.EngineInstance?.CommandCloseModel()
    this.unobserveDocumentEvents()

    if (!this.targetCanvas) return

    this.targetCanvas.parentNode?.removeChild(this.targetCanvas)

    if (typeof this.animationFrameRef === 'number') {
      window.cancelAnimationFrame(this.animationFrameRef)
    }

    window.removeEventListener('blur', this.clearPressedKeys)
  }

  public mount() {
    this.observeDocumentEvents()

    if (!this.targetCanvas) return
    this.appendCanvas(this.targetCanvas)
    this.animationFrameRef = requestAnimationFrame(this.animate)
  }

  public getModule() {
    return this.EngineInstance?.Module
  }

  private initializeEngine(module: any) {
    return new Promise<void>(async (resolve, reject) => {
      this.EngineInstance = {
        Module: module,
        Initialize: module.cwrap('Neo_Initialize', 'number', [
          'number',
          'string',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number',
          'number'
        ]),
        DeInitialize: module.cwrap('Neo_DeInitialize', 'number', []),
        DoFrame: module.cwrap('Neo_DoFrame', 'number', ['string', 'boolean']),
        CommandDebug: module.cwrap('Neo_CommandDebug', 'number', [
          'number',
          'number'
        ]),
        CommandPngCapture: module.cwrap('Neo_CommandPngCapture', 'number', [
          'number',
          'number'
        ]),
        CommandJpgCapture: module.cwrap('Neo_CommandJpgCapture', 'number', [
          'number'
        ]),
        CommandScreenshotCapture: module.cwrap(
          'Neo_CommandScreenshotCapture',
          'number',
          []
        ),
        CommandDiffusionReferenceCapture: module.cwrap(
          'Neo_CommandDiffusionReferenceCapture',
          'number',
          []
        ),
        CommandVectorCapture: module.cwrap(
          'Neo_CommandVectorCapture',
          'number',
          ['number']
        ),
        CommandMeshCapture: module.cwrap('Neo_CommandMeshCapture', 'number', [
          'number',
          'number'
        ]),
        CommandTransferMaterial: module.cwrap(
          'Neo_CommandTransferMaterial',
          'number',
          ['string', 'number']
        ),
        CommandCopyMaterial: module.cwrap('Neo_CommandCopyMaterial', 'number', [
          'string'
        ]),
        CommandPasteMaterial: module.cwrap(
          'Neo_CommandPasteMaterial',
          'number',
          ['string', 'number']
        ),
        CommandUsers_ToggleVisibility: module.cwrap(
          'Neo_CommandUsers_ToggleVisibility',
          'number',
          []
        ),
        CommandUsers_SetRemoteStatus: module.cwrap(
          'Neo_CommandUsers_SetRemoteStatus',
          'number',
          ['string']
        ),
        CommandUsers_SetRemoteLocation: module.cwrap(
          'Neo_CommandUsers_SetRemoteLocation',
          'number',
          ['string']
        ),
        CommandUsers_Get2DPosition: module.cwrap(
          'Neo_CommandUsers_Get2DPosition',
          'number',
          ['string']
        ),
        CommandUsers_SetLocalLocation: module.cwrap(
          'Neo_CommandUsers_SetLocalLocation',
          'number',
          ['string']
        ),
        CommandOpenModel: module.cwrap('Neo_CommandOpenModel', 'number', [
          'string'
        ]),
        CommandCloseModel: module.cwrap('Neo_CommandCloseModel', 'number', []),
        CommandSaveModel: module.cwrap('Neo_CommandSaveModel', 'number', []),
        CommandModelSetPose: module.cwrap('Neo_CommandModelSetPose', 'number', [
          'number'
        ]),
        CommandDelPrim: module.cwrap('Neo_CommandDelPrim', 'number', [
          'number'
        ]),
        CommandUndoRedo: module.cwrap('Neo_CommandUndoRedo', 'number', [
          'number'
        ]),
        CommandSetScaleCorners: module.cwrap(
          'Neo_CommandSetScaleCorners',
          'number',
          ['number']
        ),
        CommandGetMaterialColor: module.cwrap(
          'Neo_CommandGetMaterialColor',
          'number',
          ['string', 'number', 'number']
        ),
        CommandGetMaterialParam: module.cwrap(
          'Neo_CommandGetMaterialParam',
          'number',
          ['string', 'number', 'number']
        ),
        CommandCameraRecenter: module.cwrap(
          'Neo_CommandCameraRecenter',
          'number',
          ['number']
        ),
        CommandDuplicate: module.cwrap('Neo_CommandDuplicate', 'number', [
          'number'
        ]),
        CommandModelReorder: module.cwrap('Neo_CommandModelReorder', 'number', [
          'string',
          'number',
          'number'
        ]),
        CommandShowObjectsTogle: module.cwrap(
          'Neo_CommandShowObjectsTogle',
          'number',
          ['number']
        ),
        CommandSetSnapping: module.cwrap('Neo_CommandSetSnapping', 'number', [
          'number'
        ]),
        CommandIsSnappingEnable: module.cwrap(
          'Neo_CommandIsSnappingEnable',
          'number',
          ['number']
        ),
        Neo_CommandSetAutoFocusEnabled: module.cwrap(
          'Neo_CommandSetAutoFocusEnabled',
          'number',
          ['number']
        ),
        CommandSetActiveLayer: module.cwrap(
          'Neo_CommandSetActiveLayer',
          'number',
          ['number']
        ),
        CommandModelSelectByUUID: module.cwrap(
          'Neo_CommandModelSelectByUUID',
          'number',
          ['string', 'number']
        ),
        CommandZoomSelection: module.cwrap(
          'Neo_CommandZoomSelection',
          'number',
          ['number']
        ),
        CommandSetHistorySliderPos: module.cwrap(
          'Neo_CommandSetHistorySliderPos',
          'number',
          ['number']
        ),
        CommandSetHistoryMode: module.cwrap(
          'Neo_CommandSetHistoryMode',
          'number',
          ['number']
        ),
        CommandSetEnumValue: module.cwrap('Neo_CommandSetEnumValue', 'number', [
          'string',
          'number',
          'string'
        ]),
        CommandSetIntValue: module.cwrap('Neo_CommandSetIntValue', 'number', [
          'string',
          'number',
          'string'
        ]),
        CommandSetBoolValue: module.cwrap('Neo_CommandSetBoolValue', 'number', [
          'string',
          'number',
          'string'
        ]),
        CommandSetFloatValue: module.cwrap(
          'Neo_CommandSetFloatValue',
          'number',
          ['string', 'number', 'string']
        ),
        CommandSetDoubleValue: module.cwrap(
          'Neo_CommandSetDoubleValue',
          'number',
          ['string', 'number', 'string']
        ),
        CommandSetVec2dValue: module.cwrap(
          'Neo_CommandSetVec2dValue',
          'number',
          ['string', 'number', 'number', 'string']
        ),
        CommandSetVec3dValue: module.cwrap(
          'Neo_CommandSetVec3dValue',
          'number',
          ['string', 'number', 'number', 'number', 'string']
        ),
        CommandSetStringValue: module.cwrap(
          'Neo_CommandSetStringValue',
          'number',
          ['string', 'string', 'string']
        ),
        CommandSetIVec2Value: module.cwrap(
          'Neo_CommandSetIVec2Value',
          'number',
          ['string', 'number', 'number', 'string']
        ),
        CommandSetIVec3Value: module.cwrap(
          'Neo_CommandSetIVec3Value',
          'number',
          ['string', 'number', 'number', 'number', 'string']
        ),
        CommandBeginCommit: module.cwrap('Neo_CommandBeginCommit', null),
        CommandEndCommit: module.cwrap('Neo_CommandEndCommit', null),
        Command_AddSnapshot: module.cwrap('Neo_CommandAddSnapshot', null, [
          'string',
          'number'
        ]),
        Command_ApplySnapshot: module.cwrap(
          'Neo_CommandApplySnapshot',
          'number',
          ['number']
        ),
        Command_OverwriteSnapshot: module.cwrap(
          'Neo_CommandOverwriteSnapshot',
          'number',
          ['number']
        ),
        Command_RenameSnapshot: module.cwrap(
          'Neo_CommandRenameSnapshot',
          'number',
          ['index', 'string']
        ),
        Command_RemoveSnapshot: module.cwrap(
          'Neo_CommandRemoveSnapshot',
          'number',
          ['number']
        ),
        Command_ImportFont: module.cwrap('Neo_CommandImportFont', 'number', [
          'string',
          'number',
          'number'
        ]),
        Command_ImportSVG: module.cwrap('Neo_CommandImportSVG', 'number', [
          'string'
        ]),
        CommandModelgoDownGroupHierarchy: module.cwrap(
          'Neo_CommandModelgoDownGroupHierarchy',
          'number',
          ['string']
        ),
        CommandModelgoUpGroupHierarchy: module.cwrap(
          'Neo_CommandModelgoUpGroupHierarchy'
        ),
        CommandModelAddHoveredByUUID: module.cwrap(
          'Neo_CommandModelAddHoveredByUUID',
          'number',
          ['string']
        ),
        CommandModelRemoveHoveredByUUID: module.cwrap(
          'Neo_CommandModelRemoveHoveredByUUID',
          'number',
          ['string']
        ),
        CommandAdd2DPrimitive: module.cwrap('Neo_CommandAdd2DPrimitive', null, [
          'number'
        ]),
        enableEventsQueue: module['Neo_EnableEventsQueue'],
        Command_HandleKeyDown: module['Command_Handle_Keydown'],
        Command_HandleKeyUp: module['Command_Handle_Keyup'],
        Command_HandleMouseDown: module['Command_Handle_Mousedown'],
        Command_HandleMouseMoved: module['Command_Handle_Mousemoved'],
        Command_HandleMouseUp: module['Command_Handle_Mouseup'],
        RendererInitialize: module.cwrap('Neo_RendererInitialize', 'number', [
          'number'
        ]),
        UTF8ToString: module.UTF8ToString
      }

      const callbackRequestFont = module.addFunction((p: number) => {
        const fontId: string = module.UTF8ToString(p)

        this.EngineFontChannel.emit('request-font', {
          fontId
        } as RequestFontPayload)
      }, 'vp')

      const callbackModelLoaded = module.addFunction(
        (status: number, message: number) => {
          const data: ModelLoadedStatusEventPayload = {
            status: status,
            sceneData: module.UTF8ToString(message)
          }

          this.EngineStatusChannel.emit('status', data)
        },
        'vii'
      )

      const callbackTexturesLoaded = module.addFunction(() => {
        this.EngineDataChannel.emit('illustrativeTexturesLoaded', true)
      }, 'v')

      const sceneContentHasChangedCallback = module.addFunction(
        (
          primitiveType: number,
          selectedSceneNode: number,
          styleMode: number
        ) => {
          try {
            const data: SceneContentData = {
              primitiveType: primitiveType === -1 ? null : primitiveType,
              selectedSceneNode,
              styleMode
            }

            this.EngineSceneContentChannel.emit(
              this.engineSceneContentChannelKey,
              data
            )
          } catch (err) {
            console.error(err)
          }
        },
        'viii'
      )

      const propertyListHasChangedCallback = module.addFunction(
        (sceneData: number) => {
          try {
            const data: PropertyListHasChangedData = JSON.parse(
              module.UTF8ToString(sceneData)
            )

            this.EnginePropertyListHasChangedChannel.emit(
              this.enginePropertyListHasChangedChannelKey,
              data
            )
          } catch (err) {
            console.error(err)
          }
        },
        'vi'
      )

      const snapshotListHasChangedCallback = module.addFunction(
        (snapshotData: number) => {
          try {
            const data: SnapshotListHasChangedData = JSON.parse(
              module.UTF8ToString(snapshotData)
            )

            this.EngineSnapshotListHasChangedChannel.emit(
              this.engineSnapshotListHasChangedChannelKey,
              data
            )
          } catch (err) {
            console.error(err)
          }
        },
        'vi'
      )

      const snapshotRenamedCallback = module.addFunction(
        (snapshotData: number) => {
          try {
            const data: SnapshotListHasChangedData = JSON.parse(
              module.UTF8ToString(snapshotData)
            )

            this.EngineSnapshotRenamedChannel.emit(
              this.engineSnapshotRenamedChannelKey,
              data
            )
          } catch (err) {
            console.error(err)
          }
        },
        'vi'
      )

      const propertyHasChangedCallback = module.addFunction(
        (path: number, panelId: number, val: number) => {
          try {
            const strPath = module.UTF8ToString(path)
            const strPanelId = module.UTF8ToString(panelId)
            const strVal = module.UTF8ToString(val)

            const data: PropertyHasChangedData = {
              path: strPath,
              panelId: strPanelId,
              value: strVal
            }

            this.EnginePropertyHasChangedChannel.emit(
              this.enginePropertyHasChangedChannelKey,
              data
            )
          } catch (err) {
            console.error(err)

            const strPath = module.UTF8ToString(path)
            const strPanelId = module.UTF8ToString(panelId)
            const strVal = module.UTF8ToString(val)
            captureException(
              `Path = ${strPath}, value = ${strVal}, panelId = ${strPanelId} and error is ${JSON.stringify(
                err
              )}`
            )
          }
        },
        'viii'
      )

      const propertySoftBoundsHaveChangedCallback = module.addFunction(
        (path: number, min: number, max: number) => {
          try {
            const data: PropertySoftBoundsHaveChangedData = {
              path: module.UTF8ToString(path),
              min,
              max
            }

            this.EnginePropertySoftBoundsHaveChangedChannel.emit(
              this.enginePropertySoftBoundsHaveChangedChannelKey,
              data
            )
          } catch (err) {
            console.error(err)
          }
        },
        'viff'
      )

      const callbackSceneNavigator = module.addFunction(
        (status: number, data: number) => {
          try {
            const payload: SceneNavigatorEventPayload =
              status === 0
                ? {
                    status: 'NO_ELEMENT_SELECTED'
                  }
                : {
                    data: parseValidEngineJson(module.UTF8ToString(data)),
                    status: 'AT_LEAST_ONE_ELEMENT_SELECTED'
                  }
            this.EngineDataChannel.emit('scene-navigator-data', payload)
          } catch {
            traceInvalidJsonError(
              'engine.ts',
              'callbackSceneNavigator',
              module.UTF8ToString(data)
            )
          }
        },
        'vii'
      )

      const callbackShowPerformance = module.addFunction(
        (fps: number, mspf: number, dummy: number) => {
          this.EngineDebugChannel.emit('debug', {
            fps: fps,
            mspf: mspf,
            dummy: dummy
          })
        },
        'viii'
      )

      const callbackSetCursor = module.addFunction(
        (cursorID: number, _str_unused_: number) => {
          const cursor: Cursor = cursorID

          this.setCursor(cursor)
        },
        'vii'
      )

      const callbackCaptureReady = module.addFunction(
        (
          status: number,
          buffer: number,
          bufferSize: number,
          depthBuffer: number,
          depthBufferSize: number,
          colorBuffer: number,
          colorBufferSize: number
        ) => {
          const captureStatus: Record<number, string | null> = {
            0: null,
            1: 'jpg',
            2: 'png',
            3: 'svg',
            4: 'screenshot',
            5: 'reference',
            6: 'mesh_object'
          }

          // TODO: handle null capture status
          if (captureStatus[status] === null) {
            return
          }
          const blobArray = new Uint8Array(
            module.HEAPU8.buffer,
            buffer,
            bufferSize
          )

          if (captureStatus[status] === 'jpg') {
            const objectURL = URL.createObjectURL(
              new Blob([blobArray], { type: 'image/jpg' })
            )

            this.EngineDataChannel.emit('capture_data', {
              objectURL,
              format: 'jpg'
            } as EngineCapturePayload)
          } else if (captureStatus[status] === 'png') {
            const objectURL = URL.createObjectURL(
              new Blob([blobArray], { type: 'image/png' })
            )

            this.EngineDataChannel.emit('capture_data', {
              objectURL,
              format: 'png'
            } as EngineCapturePayload)
          } else if (captureStatus[status] === 'svg') {
            let string = ''
            for (let i = 0; i < bufferSize; i++) {
              string += String.fromCharCode(blobArray[i])
            }
            const objectURL = URL.createObjectURL(
              new Blob([string], { type: 'image/svg+xml' })
            )

            this.EngineDataChannel.emit('capture_data', {
              objectURL,
              format: 'svg'
            } as EngineCapturePayload)
          } else if (captureStatus[status] === 'screenshot') {
            const objectURL = URL.createObjectURL(
              new Blob([blobArray], { type: 'image/jpg' })
            )

            this.EngineDataChannel.emit('capture_data', {
              objectURL,
              blobArray,
              format: 'screenshot'
            } as EngineCapturePayload)
          } else if (captureStatus[status] === 'reference') {
            const depthBlobArray = new Uint8Array(
              module.HEAPU8.buffer,
              depthBuffer,
              depthBufferSize
            )

            const colorBlobArray = new Uint8Array(
              module.HEAPU8.buffer,
              colorBuffer,
              colorBufferSize
            )

            const objectURL = URL.createObjectURL(
              new Blob([blobArray], { type: 'image/png' })
            )

            const depthObjectURL = URL.createObjectURL(
              new Blob([depthBlobArray], { type: 'image/png' })
            )

            const colorObjectURL = URL.createObjectURL(
              new Blob([colorBlobArray], { type: 'image/png' })
            )

            this.EngineDataChannel.emit('capture_reference_image_data', {
              objectURL,
              blobArray,
              depthObjectURL,
              depthBlobArray,
              colorObjectURL,
              colorBlobArray,
              format: 'reference'
            } as EngineCapturePayload)
          } else if (captureStatus[status] === 'mesh_object') {
            const objectURL = URL.createObjectURL(
              new Blob([blobArray], { type: 'application/octet-stream' })
            )

            this.EngineDataChannel.emit('capture_data', {
              objectURL,
              format: 'obj'
            } as EngineCapturePayload)
          }
        },
        'viiiiiii'
      )

      const callbackSelectedObjectUI = module.addFunction(
        (status: number, centerX: number, centerY: number) => {
          this.EngineDataChannel.emit('selected-object-ui', {
            status,
            editButtonGizmoPosition: {
              centerX,
              centerY
            }
          } as SelectedObjectUI)
        },
        'viff'
      )

      const errorChannelCallback = module.addFunction(
        (group: number, error: number) => {
          console.error(
            'Error from engine: group ' + group + ', error ' + error
          )
          // TODO: handle error message and display error popup to user
        },
        'vii'
      )

      const callbackRendererInitialized = module.addFunction(
        (renderer: any) => {
          // res = 0: failed
          // res = 1: ok
          // res = 2: ok, but slow Intel GPU detected
          // res = 3: failed, wrong Neo version
          const res = this.EngineInstance?.Initialize(
            2, // Neo version
            '/engine/build/',
            callbackModelLoaded,
            callbackShowPerformance,
            callbackSceneNavigator,
            callbackSetCursor,
            callbackCaptureReady,
            callbackTexturesLoaded,
            callbackSelectedObjectUI,
            propertyHasChangedCallback,
            propertyListHasChangedCallback,
            sceneContentHasChangedCallback,
            propertySoftBoundsHaveChangedCallback,
            snapshotListHasChangedCallback,
            snapshotRenamedCallback,
            callbackRequestFont,
            errorChannelCallback,
            renderer
          )

          if (res == 0) {
            return reject()
          }

          if (res == 3) {
            console.log('module  version mismatch. Expected')
            return reject()
          }

          if (res == 2) {
            // Defer execution to end of stack to emit event to allow sagas to finish subscribing to engine event channels
            setTimeout(() => {
              this.EngineStatusChannel.emit(
                'device-status',
                DeviceStatusEventPayload.SLOW_INTEL_GPU
              )
            }, 0)
          }

          this.EngineInstance?.enableEventsQueue(false)
          this.animationFrameRef = requestAnimationFrame(this.animate)
          return resolve()
        },
        'vi'
      )

      await this.EngineInstance?.RendererInitialize(callbackRendererInitialized)
    })
  }

  private openCommitBuffer: boolean = false

  public addSnapshot(name: string, index: number) {
    return this.EngineInstance?.Command_AddSnapshot(name, index)
  }

  public applySnapshot(index: number) {
    return this.EngineInstance?.Command_ApplySnapshot(index)
  }

  public overwriteSnapshot(index: number) {
    return this.EngineInstance?.Command_OverwriteSnapshot(index)
  }

  public renameSnapshot(index: number, newName: string) {
    return this.EngineInstance?.Command_RenameSnapshot(index, newName)
  }

  public removeSnapshot(index: number) {
    return this.EngineInstance?.Command_RemoveSnapshot(index)
  }

  public importFont(fontId: string, fontBuffer: Uint8Array) {
    if (this.EngineInstance) {
      const module = this.EngineInstance.Module
      const buf = module._malloc(fontBuffer.length)
      module.HEAPU8.set(fontBuffer, buf)
      const r = this.EngineInstance.Command_ImportFont(
        fontId,
        buf,
        fontBuffer.length
      )
      module._free(buf)
      return r
    }
  }

  public importFontFailed(fontId: string) {
    this.EngineInstance?.Command_ImportFont(fontId, 0, 0)
  }

  public setActiveLayer = (layer: SceneLayer) => {
    return this.EngineInstance?.CommandSetActiveLayer(layer === '3D' ? 0 : 1)
  }

  public setPropertyPanelValue = (
    path: PropertyPanelPath,
    val: any,
    panelId: string = defaultPropertyPanelId,
    commit?: EngineCommitChange
  ): PropertyPanelSetStatus | undefined => {
    const variant: PropertyPathVariant = RootStore.getState().scene
      .propertyList[path]?.type as PropertyPathVariant

    try {
      if (!this.validatePropertyPathVariant(val, variant)) {
        throw new Error(
          `Invalid type of value of ${JSON.stringify(
            val
          )} passed in for path ${path}. Variant: ${variant}`
        )
      }

      let status: PropertyPanelSetStatus = PropertyPanelSetStatus.IncorrectType

      if (commit === EngineCommitChange.BEGIN_COMMIT) {
        this.beginCommit()
        this.openCommitBuffer = true
      }

      if (variant === 'int') {
        status = this.setPropertyPanelIntValue(path, val, panelId)
      }

      if (variant === 'bool') {
        status = this.setPropertyPanelBoolValue(path, val, panelId)
      }

      if (variant === 'EnumBase') {
        status = this.setPropertyPanelEnumValue(path, val, panelId)
      }

      if (variant === 'float') {
        status = this.setPropertyPanelFloatValue(path, val, panelId)
      }

      if (variant === 'double') {
        status = this.setPropertyPanelDoubleValue(path, val, panelId)
      }

      if (variant === 'string') {
        status = this.setPropertyPanelStringValue(path, val, panelId)
      }

      if (variant === 'vec2d') {
        status = this.setPropertyPanelVec2dValue(path, val, panelId)
      }

      if (variant === 'vec3d') {
        status = this.setPropertyPanelVec3dValue(path, val, panelId)
      }

      if (variant === 'ivec2') {
        status = this.setPropertyPanelIVec2Value(path, val, panelId)
      }

      if (variant === 'ivec3') {
        status = this.setPropertyPanelIVec3Value(path, val, panelId)
      }

      if (commit === EngineCommitChange.END_COMMIT) {
        this.endCommit()
        this.openCommitBuffer = false
      }

      return PropertyPanelSetStatus.IncorrectType
    } catch (err) {
      captureException(JSON.stringify(err)) // send to Sentry
      console.error(err)
      return PropertyPanelSetStatus.IncorrectType
    }
  }

  private validatePropertyPathVariant = (
    val: any,
    variant: PropertyPathVariant
  ): boolean => {
    switch (variant) {
      case 'bool':
        return BoolGuard(val)
      case 'EnumBase':
        return EnumBaseGuard(val)
      case 'float':
        return FloatGuard(val)
      case 'double':
        return FloatGuard(val)
      case 'int':
        return IntGuard(val)
      case 'string':
        return StringGuard(val)
      case 'vec2d':
        return Vec2dGuard(val)
      case 'vec3d':
        return Vec3dGuard(val)
      case 'ivec2':
        return IVec2Guard(val)
      case 'ivec3':
        return IVec3Guard(val)
      default:
        return false
    }
  }

  private setPropertyPanelBoolValue: PropertyPanelSetter = (
    path: string,
    val: boolean,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetBoolValue(path, Number(val), panelId)
  }

  private setPropertyPanelEnumValue: PropertyPanelSetter = (
    path: string,
    val: number,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetEnumValue(path, val, panelId)
  }

  private setPropertyPanelIntValue: PropertyPanelSetter = (
    path: string,
    val: number,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetIntValue(path, val, panelId)
  }

  private setPropertyPanelFloatValue: PropertyPanelSetter = (
    path: string,
    val: number,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetFloatValue(path, val, panelId)
  }

  private setPropertyPanelDoubleValue: PropertyPanelSetter = (
    path: string,
    val: number,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetDoubleValue(path, val, panelId)
  }

  private setPropertyPanelVec2dValue: PropertyPanelSetter = (
    path: string,
    val: Vec2d,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetVec2dValue(
      path,
      val.x,
      val.y,
      panelId
    )
  }

  private setPropertyPanelVec3dValue: PropertyPanelSetter = (
    path: string,
    val: Vec3d,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetVec3dValue(
      path,
      val.x,
      val.y,
      val.z,
      panelId
    )
  }

  private setPropertyPanelStringValue: PropertyPanelSetter = (
    path: string,
    val: string,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetStringValue(path, val, panelId)
  }

  private setPropertyPanelIVec2Value: PropertyPanelSetter = (
    path: string,
    val: IVec2,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetIVec2Value(
      path,
      val.x,
      val.y,
      panelId
    )
  }

  private setPropertyPanelIVec3Value: PropertyPanelSetter = (
    path: string,
    val: IVec3,
    panelId: string = defaultPropertyPanelId
  ) => {
    return this.EngineInstance?.CommandSetIVec3Value(
      path,
      val.x,
      val.y,
      val.z,
      panelId
    )
  }

  private beginCommit = () => this.EngineInstance?.CommandBeginCommit()
  private endCommit = () => this.EngineInstance?.CommandEndCommit()

  private animate() {
    try {
      if (!this.targetCanvas) return
      // make sure the canvas size has exactly one pixel per device pixel
      const dpr = window.devicePixelRatio || 1

      const r = this.targetCanvas?.getBoundingClientRect()
      const displayWidth = Math.round(r.right * dpr) - Math.round(r.left * dpr)
      const displayHeight = Math.round(r.bottom * dpr) - Math.round(r.top * dpr)
      this.targetCanvas.width = displayWidth
      this.targetCanvas.height = displayHeight

      this.EngineInstance?.DoFrame(
        this.EngineFeatures,
        this.renderOnlyFrameContent
      )

      this.animationFrameRef = requestAnimationFrame(this.animate)
    } catch (e) {
      console.error(e)
    }
  }

  public setRenderOnlyFrameContent = (value: boolean) => {
    this.renderOnlyFrameContent = value
  }

  public updateFeatures(features: string) {
    this.EngineFeatures = features
  }

  public add2DPrimitive = (primitiveType: Engine2DPrimitive) => {
    this.EngineInstance?.CommandAdd2DPrimitive(primitiveType)
  }

  public async loadDocument(uri: string): Promise<number> {
    const accessToken: ITokenInformation | null =
      await AdobeIMS.getAccessTokenAsync()

    const response = await fetch(uri, {
      headers: {
        authorization: `Bearer ${accessToken?.token!}`,
        'X-Api-Key': process.env.NEXT_PUBLIC_ADOBE_IMS_CLIENT_ID
      }
    }).then(res => res.text())

    const blobURL = URL.createObjectURL(
      new Blob([response], { type: 'application/json' })
    )

    const result = this.EngineInstance?.CommandOpenModel(blobURL)

    URL.revokeObjectURL(blobURL)

    return result
  }

  public getDocument(): string {
    const model = this.EngineInstance?.CommandSaveModel()
    return this.EngineInstance?.UTF8ToString(model)
  }

  public setLocalUserLocation(location: LocalUserLocation): number {
    const data = JSON.stringify(location)
    return this.EngineInstance?.CommandUsers_SetLocalLocation(data)
  }

  public getRemoteUserCursorPosition(uuid: string): number {
    return this.EngineInstance?.CommandUsers_Get2DPosition(uuid)
  }

  public setRemoteUserStatus(status: RemoteUserStatus): number {
    const data = JSON.stringify(status)
    return this.EngineInstance?.CommandUsers_SetRemoteStatus(data)
  }

  public setRemoteUserLocation(location: RemoteUserLocation): number {
    const data = JSON.stringify(location)
    return this.EngineInstance?.CommandUsers_SetRemoteLocation(data)
  }

  public toggleUsersAvatarVisibility() {
    return this.EngineInstance?.CommandUsers_ToggleVisibility()
  }

  public duplicatePrimitive(): number {
    return this.EngineInstance?.CommandDuplicate(-1)
  }

  public deletePrimitive(): number {
    return this.EngineInstance?.CommandDelPrim(-1)
  }

  public setPrimitiveScaleCorners(value: boolean) {
    return this.EngineInstance?.CommandSetScaleCorners(value ? 1 : 0)
  }

  public getMaterialProperties(address?: string) {
    if (!this.EngineInstance) return null
    const color = this.EngineInstance.CommandGetMaterialColor(
      address,
      MaterialColorParam.COLOR,
      EngineMode.NORMAL
    )
    const top = this.EngineInstance.CommandGetMaterialColor(
      address,
      MaterialColorParam.E_COLOR_TOP,
      EngineMode.OUTLINE
    )
    const lig = this.EngineInstance.CommandGetMaterialColor(
      address,
      MaterialColorParam.E_COLOR_LIG,
      EngineMode.OUTLINE
    )
    const sha = this.EngineInstance.CommandGetMaterialColor(
      address,
      MaterialColorParam.E_COLOR_SHA,
      EngineMode.OUTLINE
    )
    const i_color = this.EngineInstance.CommandGetMaterialColor(
      address,
      MaterialColorParam.I_COLOR,
      EngineMode.ILLUSTRATIVE
    )
    const i_emissive_color = this.EngineInstance.CommandGetMaterialColor(
      address,
      MaterialColorParam.I_EMISSIVE_COLOR,
      EngineMode.ILLUSTRATIVE
    )

    if ([color, top, lig, sha].some((color: number) => color === -1))
      return null
    const properties = {
      materialESpecularIntensity: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.E_SPECULAR_INTENSITY,
        EngineMode.OUTLINE
      ) as number,
      materialESpecularSize: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.E_SPECULAR_SIZE,
        EngineMode.OUTLINE
      ) as number,
      materialMetalness: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.METALNESS,
        EngineMode.NORMAL
      ) as number,
      materialOpacity: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.OPACITY,
        EngineMode.NORMAL
      ) as number,
      materialReflective: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.REFLECTIVE,
        EngineMode.NORMAL
      ) as number,
      materialRoughness: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.ROUGHNESS,
        EngineMode.NORMAL
      ) as number,
      materialIStrokeSize: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_STROKE_SIZE,
        EngineMode.ILLUSTRATIVE
      ) as number,
      materialIHighlightIntensity: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_HIGHLIGHT_INTENSITY,
        EngineMode.ILLUSTRATIVE
      ) as number,
      materialIStrokeIntensity: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_STROKE_INTENSITY,
        EngineMode.ILLUSTRATIVE
      ) as number,
      materialIColorVarIntensity: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_STROKE_INTENSITY,
        EngineMode.ILLUSTRATIVE
      ) as number,
      materialIScaleX: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_SCALE_X,
        EngineMode.ILLUSTRATIVE
      ) as number,
      materialIScaleY: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_SCALE_Y,
        EngineMode.ILLUSTRATIVE
      ) as number,
      materialIAngle: this.EngineInstance.CommandGetMaterialParam(
        address,
        MaterialParam.I_ANGLE,
        EngineMode.ILLUSTRATIVE
      ) as number
    }
    if (Object.values(properties).some((value: number) => value === -1))
      return null
    return {
      ...properties,
      materialColor: this.numHexToStrHex(color),
      materialEColorTop: this.numHexToStrHex(top),
      materialEColorLig: this.numHexToStrHex(lig),
      materialEColorSha: this.numHexToStrHex(sha),
      materialIColor: this.numHexToStrHex(i_color),
      materialIEmissiveColor: this.numHexToStrHex(i_emissive_color)
    }
  }

  public transferMaterials() {
    return this.EngineInstance?.CommandTransferMaterial(null, 5)
  }

  public copyMaterials() {
    return this.EngineInstance?.CommandCopyMaterial(null)
  }

  public pasteMaterials() {
    return this.EngineInstance?.CommandPasteMaterial(null, 5)
  }

  public recenterCamera() {
    return this.EngineInstance?.CommandCameraRecenter(-1)
  }

  public zoomSelection(factor: number) {
    return this.EngineInstance?.CommandZoomSelection(factor)
  }

  public handleUndoRedo(value: EngineUndoRedo) {
    if (this.openCommitBuffer) {
      this.endCommit()
    }

    this.EngineInstance?.CommandUndoRedo(value)
  }

  public toggleShowObjectsOutline(): number {
    return this.EngineInstance?.CommandShowObjectsTogle()
  }

  public setModelPose(value: GroupExpanded) {
    return this.EngineInstance?.CommandModelSetPose(value)
  }

  public reorderStackElements({
    srcIndices,
    destIndex,
    mode
  }: EngineStackReorderingOpts) {
    // Pass the array of src indices as a string
    const srcIndicesAsString = srcIndices.join(' ')
    return this.EngineInstance?.CommandModelReorder(
      srcIndicesAsString,
      destIndex,
      mode
    )
  }

  public setAutoFocusEnabled(enabled: boolean) {
    return this.EngineInstance?.Neo_CommandSetAutoFocusEnabled(enabled ? 1 : 0)
  }

  public setSnapping(enabled: boolean) {
    return this.EngineInstance?.CommandSetSnapping(enabled ? 1 : 0)
  }

  public isSnappingEnable(enabled: boolean): number {
    return this.EngineInstance?.CommandIsSnappingEnable(0)
  }

  public selectElementByUUID(uuid: string, multiSelect?: boolean) {
    return this.EngineInstance?.CommandModelSelectByUUID(
      uuid,
      multiSelect ? 2 : 1
    )
  }

  public unselectElementByUUID(uuid: string) {
    return this.EngineInstance?.CommandModelSelectByUUID(uuid, 2)
  }

  public apply2DFilter = (filterType: number) => {
    // TODO: Figure out why 3 is hard coded and fix it (JGAFAR)
    return this.EngineInstance?.CommandDebug(3, filterType)
  }

  public exportCapture(
    payload: EngineExportCapturePayload
  ): number | undefined {
    switch (payload.format) {
      case 'svg':
        return this.EngineInstance?.CommandVectorCapture(
          payload.splitFaceEnabled
        )
      case 'png':
        return this.EngineInstance?.CommandPngCapture(
          payload.compressionLevel === undefined ? 8 : payload.compressionLevel,
          payload.transparentBackground
        )
      case 'jpg':
        return this.EngineInstance?.CommandJpgCapture(
          payload.quality === undefined ? 100 : payload.quality
        )
      case 'screenshot':
        return this.EngineInstance?.CommandScreenshotCapture()
      case 'reference':
        return this.EngineInstance?.CommandDiffusionReferenceCapture()
      case 'obj':
        return this.EngineInstance?.CommandMeshCapture(0, 2) // format: 0=OBJ, 1=STL(ascii), 2=STL(binary)       quality: 0=Low, 1=Medium, 2=High
      default:
        return
    }
  }

  // TODO: refactor to a color module
  public hexColorToEngineColor(hex: string): [number, number, number] {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)

    if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
      throw new Error('All rgb values must be between 0 and 255, inclusive')
    }

    return [r, g, b]
  }

  private numHexToStrHex(num: number) {
    return this.rGBToHex(num >> 16, (num >> 8) & 0xff, num & 0xff)
  }

  // TODO: refactor to a color module
  public rGBToHex(r: number, g: number, b: number) {
    function componentToHex(c: number) {
      let hex = c.toString(16)
      return hex.length == 1 ? '0' + hex : hex
    }

    function roundToNearestValidInteger(n: number) {
      return Math.min(Math.round(n), 255)
    }

    r = roundToNearestValidInteger(r)
    g = roundToNearestValidInteger(g)
    b = roundToNearestValidInteger(b)

    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`
  }

  public getCurrentCursor(): string | undefined {
    const targetCanvas = document.getElementById('canvas') as HTMLCanvasElement
    const { style } = targetCanvas
    return style.cursor
  }

  public setCursor(cursor: Cursor) {
    const targetCanvas = document.getElementById('canvas') as HTMLCanvasElement
    if (targetCanvas) {
      const { style } = targetCanvas

      const url = cursorRegistry.urlForCursor(cursor)
      if (style && url) style.cursor = url
    }
  }

  public setHistorySliderPos(value: number) {
    return this.EngineInstance?.CommandSetHistorySliderPos(value)
  }

  public setHistoryMode(value: number) {
    return this.EngineInstance?.CommandSetHistoryMode(value)
  }

  public importSVG(svg: string) {
    return this.EngineInstance?.Command_ImportSVG(svg)
  }

  public goDownGroupHierarchy(uuid: string) {
    return this.EngineInstance?.CommandModelgoDownGroupHierarchy(uuid)
  }

  public goUpGroupHierarchy() {
    return this.EngineInstance?.CommandModelgoUpGroupHierarchy()
  }

  public mouseEnterObject(uuid: string) {
    return this.EngineInstance?.CommandModelAddHoveredByUUID(uuid)
  }

  public mouseLeaveObject(uuid: string) {
    return this.EngineInstance?.CommandModelRemoveHoveredByUUID(uuid)
  }
}
